/**
 * Phase 9 chunk 4 — pure-math helpers for the decay-trace overlay.
 *
 * Cesium-free + DOM-free so the alpha curve + trace-record-validation
 * live in Vitest. The Cesium polyline construction is exercised in
 * Playwright.
 */

export interface DecayTraceRecord {
  norad_id: number;
  name: string;
  /** ISO-8601 timestamp from the reentries.predicted_decay column. */
  predicted_decay: string;
  /** Raw TLE line 1 for satellite.js. */
  line1: string;
  /** Raw TLE line 2 for satellite.js. */
  line2: string;
  /** ISO-8601 epoch of the TLE — used to flag stale propagations. */
  tle_epoch: string;
  /** TIP-message risk score in [0,1], or null when the source lacked it. */
  risk_score: number | null;
}

/**
 * Wall-clock window we propagate around each predicted decay.
 *
 * Locked in docs/phase9.md § II row 6: "each reentry's last ~24 h
 * of orbit before TIP-message-predicted decay." Half-window of 12 h
 * → 24 h total → 24 ground-track sweeps for a ~90-min orbiter.
 */
export const DECAY_HALF_WINDOW_MS = 12 * 60 * 60_000;
export const DECAY_SAMPLES_PER_TRACE = 120; // ~12 min per sample over 24 h
export const DECAY_SEGMENT_COUNT = 24;

/** Hard cap on simultaneous traces (matches the server-side limit). */
export const DECAY_MAX_TRACES = 50;

/** Max alpha along the trace — these are historical context, not focus. */
export const DECAY_MAX_ALPHA = 0.45;
export const DECAY_MIN_ALPHA = 0.05;

/** Trace hue per docs/phase9.md § III chunk 4 ("fading red trace"). */
export const DECAY_HUE = '#ff3860';

/**
 * Alpha falloff: brightest at the decay moment, fading rapidly back
 * 24 hours before. The decay itself is the focal point, so the
 * future direction (forward of the decay event) gets zero
 * contribution — TIP messages don't predict post-decay orbit.
 *
 * Pure math: defensive against zero / NaN half-window inputs so a
 * malformed `predicted_decay` doesn't poison rendering.
 */
export function decayTraceAlpha(deltaToDecayMs: number, halfWindowMs: number = DECAY_HALF_WINDOW_MS): number {
  if (halfWindowMs <= 0 || !Number.isFinite(halfWindowMs)) return DECAY_MIN_ALPHA;
  if (!Number.isFinite(deltaToDecayMs)) return DECAY_MIN_ALPHA;
  // Negative delta = pre-decay (history); positive = post-decay (drop
  // to the floor because TIP messages stop being meaningful past the
  // event itself).
  if (deltaToDecayMs > 0) return DECAY_MIN_ALPHA;
  const absRel = Math.min(1, Math.abs(deltaToDecayMs) / halfWindowMs);
  const alpha = DECAY_MAX_ALPHA - (DECAY_MAX_ALPHA - DECAY_MIN_ALPHA) * absRel;
  return Math.max(DECAY_MIN_ALPHA, Math.min(DECAY_MAX_ALPHA, alpha));
}

/**
 * Defensive validation of the `/api/v1/reentries/traces` response.
 * Drops rows missing required fields, dedups by NORAD (first record
 * wins), and caps at {@link DECAY_MAX_TRACES} even if the server
 * returned more (defense in depth against drift between client cap
 * and server cap).
 *
 * @param raw  Untrusted server payload (the `data` array).
 * @param warn Injectable logger for the over-cap warning.
 */
export function validateTraces(raw: unknown, warn: (msg: string) => void = console.warn): DecayTraceRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: DecayTraceRecord[] = [];
  const seen = new Set<number>();
  for (const row of raw) {
    if (typeof row !== 'object' || row === null) continue;
    const norad = (row as { norad_id?: unknown }).norad_id;
    const name  = (row as { name?: unknown }).name;
    const decay = (row as { predicted_decay?: unknown }).predicted_decay;
    const l1    = (row as { line1?: unknown }).line1;
    const l2    = (row as { line2?: unknown }).line2;
    const epoch = (row as { tle_epoch?: unknown }).tle_epoch;
    const risk  = (row as { risk_score?: unknown }).risk_score;
    if (!Number.isInteger(norad) || (norad as number) <= 0) continue;
    if (typeof name !== 'string' || name.trim() === '') continue;
    if (typeof decay !== 'string' || decay === '') continue;
    if (typeof l1 !== 'string' || typeof l2 !== 'string') continue;
    if (typeof epoch !== 'string') continue;
    if (seen.has(norad as number)) continue;
    seen.add(norad as number);
    out.push({
      norad_id: norad as number,
      name,
      predicted_decay: decay,
      line1: l1,
      line2: l2,
      tle_epoch: epoch,
      risk_score: typeof risk === 'number' && Number.isFinite(risk) ? risk : null,
    });
  }
  if (out.length > DECAY_MAX_TRACES) {
    warn(`[decayTraceMath] server returned ${out.length} traces; capping at ${DECAY_MAX_TRACES} (see docs/phase9.md § II row 6)`);
    out.length = DECAY_MAX_TRACES;
  }
  return out;
}
